import { useState, useEffect, useRef, useCallback } from "react";

/* ══════════════════════════════════════════════════════════════
   BREAKPOINTS
══════════════════════════════════════════════════════════════ */
function useBreakpoint() {
  const get = () => {
    const w = typeof window !== "undefined" ? window.innerWidth : 1280;
    if (w < 640) return "mobile";
    if (w < 1024) return "tablet";
    return "desktop";
  };
  const [bp, setBp] = useState(get);
  useEffect(() => {
    const fn = () => setBp(get());
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return bp;
}

/* ══════════════════════════════════════════════════════════════
   DATA  — every entry has a companyUrl; projects have a liveUrl
══════════════════════════════════════════════════════════════ */
const NAV_SECTIONS = ["About", "Experience", "Portfolio"];

const EXPERIENCES = [
  {
    period: "2024 — PRESENT",
    role: "Lead Full-Stack Engineer",
    company: "Capital One",
    companyUrl: "https://www.capitalone.com",
    description:
      "Architecting scalable financial platforms serving millions of customers. Leading a cross-functional team of 8 engineers, driving React + Node.js modernisation and AWS infrastructure optimisation.",
    metrics: ["8-engineer team", "Millions of users", "AWS infra lead"],
    tags: ["React", "Node.js", "Python", "AWS", "TypeScript", "System Design"],
  },
  {
    period: "2022 — 2024",
    role: "Sr. Front End Engineer",
    company: "Southwest Airlines",
    companyUrl: "https://www.southwest.com",
    description:
      "Redesigned the Rapid Rewards My Account page with a mobile-first React.js architecture using Storybook. Delivered a cohesive experience across web and native platforms for millions of loyalty members.",
    metrics: ["↑ NPS +22 pts", "Mobile-first", "Storybook components"],
    tags: ["React.js", "Python", "Node.js", "Storybook", "Mobile-First", "Component Design"],
  },
  {
    period: "2020 — 2022",
    role: "Sr. Front End Engineer",
    company: "Verizon",
    companyUrl: "https://www.verizon.com",
    description:
      "Led full migration of the Small Business digital platform to a responsive, modern stack. Streamlined checkout UX, cutting cart abandonment and increasing conversion through performance-first engineering.",
    metrics: ["↓ Cart abandon rate", "↑ Conversion rate", "Full migration lead"],
    tags: ["React", "Responsive Design","Node.js", "Performance", "UX Engineering"],
  },
  {
    period: "2018 — 2020",
    role: "Full Stack Engineer",
    company: "PayPal",
    companyUrl: "https://www.paypal.com",
    description:
      "Built a robust, admin-driven newsletter platform for customer communications — account alerts, security notifications, and product updates. Delivered pixel-perfect rendering across all major email clients.",
    metrics: ["Cross-client rendering", "Admin-driven templates", "Security comms"],
    tags: ["Full Stack", "Node.js", "Email Engineering", "HTML/CSS"],
  },
  {
    period: "2016 — 2018",
    role: "Sr. Front End Engineer",
    company: "Apple",
    companyUrl: "https://www.apple.com",
    description:
      "Designed and delivered a real-time LSP network operations dashboard with interactive data-centre mapping. Replaced brittle CLI tooling with a live, visual monitoring interface that cut incident response time significantly.",
    metrics: ["Real-time monitoring", "↓ Incident response time", "Interactive map"],
    tags: ["React", "Data Visualisation", "Operations", "Real-time"],
  },
  {
    period: "2013 — 2016",
    role: "Front End Developer",
    company: "Delta Dental Insurance",
    companyUrl: "https://www.deltadentalins.com",
    description:
      "Built patient-facing portals and internal tooling with a focus on accessibility and clean, maintainable JavaScript. Introduced component patterns that improved team velocity across multiple product lines.",
    metrics: ["WCAG accessibility", "Component library", "Multi-product"],
    tags: ["JavaScript", "CSS3", "HTML5", "Accessibility"],
  },
  {
    period: "2011 — 2013",
    role: "Front End Developer",
    company: "T-Mobile",
    companyUrl: "https://www.t-mobile.com",
    description:
      "Delivered consumer-facing web experiences for one of the nation's largest wireless carriers, focusing on performance, responsive layouts, and frictionless cross-device user journeys.",
    metrics: ["Nationwide scale", "Cross-device", "Performance-first"],
    tags: ["HTML5", "CSS3", "JavaScript", "Responsive"],
  },
];

const PROJECTS = [
  {
    title: "Verizon Small Business — Platform Modernisation",
    company: "Verizon",
    description:
      "Led end-to-end migration from a legacy stack to a performant, responsive React platform. Simplified the small business checkout flow, improving conversion rates and aligning with Verizon brand standards.",
    liveUrl: "https://digitalready.verizonwireless.com/onboarding",
    companyUrl: "https://www.verizon.com",
    tags: ["React", "Responsive", "E-Commerce", "UX Engineering"],
    stat: "↑ Conversion",
  },
  {
    title: "Southwest Airlines — Rapid Rewards My Account",
    company: "Southwest Airlines",
    description:
      "Mobile-first rebuild of the Rapid Rewards member hub — points overview, profile management, and trip dashboard — using React.js and Storybook for a scalable, consistent component system.",
    liveUrl: "https://mobile.southwest.com/loyalty/myaccount/",
    companyUrl: "https://www.southwest.com",
    tags: ["React.js", "Storybook", "Mobile-Native", "Loyalty"],
    stat: "↑ NPS +22",
  },
  {
    title: "PayPal — Customer Communication Platform",
    company: "PayPal",
    description:
      "Engineered a flexible admin-controlled newsletter system for account updates, security alerts, and product communications. Ensured flawless HTML/CSS rendering across every major email client.",
    liveUrl: null,
    companyUrl: "https://www.paypal.com",
    tags: ["Email Engineering", "Node.js", "Admin Tools", "HTML/CSS"],
    stat: "Cross-client ✓",
  },
  {
    title: "Apple — Network Operations Dashboard",
    company: "Apple",
    description:
      "Built a real-time LSP transmission monitoring dashboard replacing legacy CLI tools. An interactive data-centre map surfaced live downtime states, dramatically accelerating the ops team's incident response.",
    liveUrl: null,
    companyUrl: "https://www.apple.com",
    tags: ["Real-time", "Data Visualisation", "Operations", "React"],
    stat: "↓ Incident time",
  },
];

const SKILLS = [
  "React.js", "Vue.js", "Angular", "React Native",
  "Node.js", "Python", "TypeScript", "JavaScript",
  "HTML5 / CSS3", "AWS", "GraphQL", "System Design",
];

/* ══════════════════════════════════════════════════════════════
   COLOURS
══════════════════════════════════════════════════════════════ */
const C = {
  bg: "#0a0a0c",
  surface: "#111114",
  surfaceHover: "#16161b",
  border: "#1c1c22",
  borderHover: "rgba(228,201,126,0.18)",
  gold: "#e4c97e",
  goldDim: "#9a7a38",
  goldGlow: "rgba(228,201,126,0.08)",
  text: "#dddde3",
  muted: "#6e6e82",
  mutedLight: "#9898ae",
  white: "#f4f4f6",
};

/* ══════════════════════════════════════════════════════════════
   GLOBAL CSS
══════════════════════════════════════════════════════════════ */
const GCSS = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { background: ${C.bg}; color: ${C.text}; -webkit-font-smoothing: antialiased; }

  ::-webkit-scrollbar { width: 3px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: #222230; border-radius: 2px; }

  /* Nav */
  .sa-nbtn:hover .sa-nline { width: 48px !important; background: ${C.gold} !important; }
  .sa-nbtn:hover .sa-nlabel { color: ${C.gold} !important; }
  .sa-social:hover { color: ${C.gold} !important; }

  /* Experience cards */
  .sa-exp-card {
    transition: background 0.22s ease, border-color 0.22s ease, opacity 0.22s ease,
                transform 0.22s ease;
    border-radius: 6px;
    border: 1px solid transparent;
  }
  .sa-exp-list:hover .sa-exp-card:not(:hover) { opacity: 0.32; }
  .sa-exp-card:hover {
    background: ${C.surfaceHover} !important;
    border-color: ${C.borderHover} !important;
    transform: translateX(3px);
  }

  /* Company link inside card */
  .sa-company-link {
    color: ${C.gold};
    text-decoration: none;
    font-style: italic;
    transition: opacity 0.15s;
    display: inline-flex; align-items: center; gap: 3px;
  }
  .sa-company-link:hover { opacity: 0.75; text-decoration: underline; text-underline-offset: 3px; }

  /* Project cards */
  .sa-proj-card {
    transition: background 0.22s ease, border-color 0.22s ease, opacity 0.22s ease,
                transform 0.22s ease;
    border-radius: 6px;
    border: 1px solid transparent;
    text-decoration: none;
    display: flex;
    color: inherit;
  }
  .sa-proj-list:hover .sa-proj-card:not(:hover) { opacity: 0.32; }
  .sa-proj-card:hover {
    background: ${C.surfaceHover} !important;
    border-color: ${C.borderHover} !important;
    transform: translateY(-2px);
  }
  .sa-proj-card:hover .sa-proj-arrow { transform: translate(3px,-3px); }

  .sa-proj-arrow { transition: transform 0.2s ease; display: inline-flex; }

  .sa-live-badge {
    display: inline-flex; align-items: center; gap: 4px;
    font-size: 10px; font-family: 'Courier New', monospace; letter-spacing: 0.1em;
    color: ${C.gold}; border: 1px solid rgba(228,201,126,0.3);
    padding: 3px 9px; border-radius: 3px;
    background: rgba(228,201,126,0.04);
    transition: background 0.15s;
    text-decoration: none; flex-shrink: 0; white-space: nowrap;
  }
  .sa-live-badge:hover { background: rgba(228,201,126,0.12); }

  /* Metric pills */
  .sa-metric {
    font-size: 10px; font-family: 'Courier New', monospace; letter-spacing: 0.06em;
    color: ${C.mutedLight}; border: 1px solid ${C.border};
    padding: 2px 9px; border-radius: 3px;
    background: rgba(255,255,255,0.02);
  }

  /* Mobile overlay */
  .sa-overlay {
    position: fixed; inset: 0; z-index: 200;
    background: rgba(10,10,12,0.98);
    display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 24px;
    transform: translateX(100%);
    transition: transform 0.3s cubic-bezier(0.4,0,0.2,1);
  }
  .sa-overlay.open { transform: translateX(0); }

  .sa-mnav {
    background: none; border: none; cursor: pointer;
    font-family: 'Courier New', monospace; font-size: 13px;
    letter-spacing: 0.22em; text-transform: uppercase;
    color: ${C.muted}; transition: color 0.2s; padding: 8px 0;
  }
  .sa-mnav:hover, .sa-mnav.act { color: ${C.gold}; }

  .sa-hbl {
    display: block; width: 22px; height: 1px;
    background: ${C.mutedLight}; transition: transform 0.3s, opacity 0.3s;
  }

  /* Section anchor offset */
  .sa-sec { scroll-margin-top: 80px; }
  @media (max-width: 639px) { .sa-sec { scroll-margin-top: 64px; } }

  /* Stat badge on project */
  .sa-stat {
    font-size: 10px; font-family: 'Courier New', monospace; letter-spacing: 0.08em;
    color: ${C.gold}; padding: 2px 8px; border-radius: 3px;
    background: rgba(228,201,126,0.08); border: 1px solid rgba(228,201,126,0.18);
    white-space: nowrap; flex-shrink: 0;
  }

  /* Subtle top-border accent on section labels */
  .sa-divider {
    width: 100%; height: 1px;
    background: linear-gradient(90deg, ${C.gold} 0%, transparent 60%);
    margin-bottom: 36px;
    opacity: 0.35;
  }

  /* Cursor: pointer on entire exp card if it has a link */
  .sa-exp-card.clickable { cursor: pointer; }
`;

function useGlobalStyle() {
  useEffect(() => {
    if (document.getElementById("sa-g")) return;
    const el = document.createElement("style");
    el.id = "sa-g"; el.textContent = GCSS;
    document.head.appendChild(el);
    return () => document.getElementById("sa-g")?.remove();
  }, []);
}

/* ══════════════════════════════════════════════════════════════
   ICONS
══════════════════════════════════════════════════════════════ */
const LI = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-4 0v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/><circle cx="4" cy="4" r="2"/></svg>;
const GH = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 00-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0020 4.77 5.07 5.07 0 0019.91 1S18.73.65 16 2.48a13.38 13.38 0 00-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 005 4.77a5.44 5.44 0 00-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 009 18.13V22"/></svg>;
const EM = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>;
const EXT = ({ size = 13 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>;
const ARR = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/></svg>;

/* ══════════════════════════════════════════════════════════════
   ATOMS
══════════════════════════════════════════════════════════════ */
function Socials({ gap = 18 }) {
  return (
    <div style={{ display: "flex", gap }}>
      {[
        { href: "https://www.linkedin.com/in/suhaib-siddiqui?utm_source=share_via&utm_content=profile&utm_medium=member_ios", icon: <LI />, label: "LinkedIn" },
        // { href: "https://github.com/", icon: <GH />, label: "GitHub" },
        { href: "mailto:suhaibasmohammed@gmail.com", icon: <EM />, label: "Email" },
      ].map(({ href, icon, label }) => (
        <a key={label} href={href} target="_blank" rel="noreferrer" aria-label={label}
          className="sa-social"
          style={{ color: C.muted, textDecoration: "none", display: "flex", alignItems: "center", transition: "color 0.2s" }}>
          {icon}
        </a>
      ))}
    </div>
  );
}

function SectionHead({ text }) {
  return (
    <div style={{ marginBottom: "36px" }}>
      <div className="sa-divider" />
      <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
        <span style={{ width: "30px", height: "1px", background: C.gold, display: "block", flexShrink: 0 }} />
        <span style={{ fontSize: "10px", fontFamily: "'Courier New',monospace", letterSpacing: "0.24em", textTransform: "uppercase", color: C.gold }}>
          {text}
        </span>
      </div>
    </div>
  );
}

function Tags({ tags }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
      {tags.map(t => (
        <span key={t} style={{ fontSize: "10px", fontFamily: "'Courier New',monospace", letterSpacing: "0.07em",
          color: C.gold, border: "1px solid rgba(228,201,126,0.2)", padding: "3px 9px", borderRadius: "3px",
          background: "rgba(228,201,126,0.04)" }}>
          {t}
        </span>
      ))}
    </div>
  );
}

function Badge() {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "10px",
      fontFamily: "'Courier New',monospace", letterSpacing: "0.12em", textTransform: "uppercase",
      color: C.gold, border: `1px solid ${C.goldDim}`, padding: "4px 12px", borderRadius: "3px",
      background: C.goldGlow }}>
      <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: C.gold,
        display: "inline-block", animation: "pulse 2s infinite" }} />
      Available for opportunities
    </span>
  );
}

/* ══════════════════════════════════════════════════════════════
   MOBILE HEADER
══════════════════════════════════════════════════════════════ */
function MobileHeader({ active, onNav }) {
  const [open, setOpen] = useState(false);
  const go = id => { setOpen(false); onNav(id); };
  return (
    <>
      <header style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        background: "rgba(10,10,12,0.96)", backdropFilter: "blur(16px)",
        borderBottom: `1px solid ${C.border}`, height: "56px", padding: "0 20px",
        display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <a href="/" style={{ textDecoration: "none" }}>
          <span style={{ fontSize: "15px", fontStyle: "italic", color: C.white, fontFamily: "'Georgia',serif" }}>
            Suhaib Ahmed
          </span>
        </a>
        <button onClick={() => setOpen(o => !o)} aria-label="Menu"
          style={{ background: "none", border: "none", cursor: "pointer", display: "flex", flexDirection: "column", gap: "5px", padding: "4px" }}>
          <span className="sa-hbl" style={{ transform: open ? "rotate(45deg) translate(4px,4px)" : "none" }} />
          <span className="sa-hbl" style={{ opacity: open ? 0 : 1 }} />
          <span className="sa-hbl" style={{ transform: open ? "rotate(-45deg) translate(4px,-4px)" : "none" }} />
        </button>
      </header>
      <nav className={`sa-overlay${open ? " open" : ""}`}>
        {NAV_SECTIONS.map(s => (
          <button key={s} className={`sa-mnav${active === s.toLowerCase() ? " act" : ""}`}
            onClick={() => go(s.toLowerCase())}>{s}</button>
        ))}
        <Socials gap={22} />
      </nav>
    </>
  );
}

/* ══════════════════════════════════════════════════════════════
   TABLET HEADER
══════════════════════════════════════════════════════════════ */
function TabletHeader({ active, onNav }) {
  return (
    <header style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
      background: "rgba(10,10,12,0.96)", backdropFilter: "blur(16px)",
      borderBottom: `1px solid ${C.border}`, height: "60px", padding: "0 40px",
      display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <a href="/" style={{ textDecoration: "none", display: "flex", alignItems: "baseline", gap: "10px" }}>
        <span style={{ fontSize: "16px", fontStyle: "italic", color: C.white, fontFamily: "'Georgia',serif" }}>Suhaib Ahmed</span>
        <span style={{ fontSize: "10px", fontFamily: "'Courier New',monospace", color: C.gold, letterSpacing: "0.1em" }}>Lead Full Stack Engineer</span>
      </a>
      <nav style={{ display: "flex", gap: "28px", alignItems: "center" }}>
        {NAV_SECTIONS.map(s => {
          const on = active === s.toLowerCase();
          return (
            <button key={s} onClick={() => onNav(s.toLowerCase())}
              style={{ background: "none", border: "none", cursor: "pointer",
                fontFamily: "'Courier New',monospace", fontSize: "10px", letterSpacing: "0.16em",
                textTransform: "uppercase", color: on ? C.gold : C.muted, transition: "color 0.2s",
                borderBottom: on ? `1px solid ${C.gold}` : "1px solid transparent", paddingBottom: "2px" }}>
              {s}
            </button>
          );
        })}
        <Socials gap={16} />
      </nav>
    </header>
  );
}

/* ══════════════════════════════════════════════════════════════
   IDENTITY BLOCK (mobile + tablet top)
══════════════════════════════════════════════════════════════ */
function IdentityBlock({ isMobile }) {
  const nameSz = isMobile ? "34px" : "44px";
  return (
    <div style={{ marginBottom: isMobile ? "48px" : "56px", paddingTop: "6px" }}>
      <div style={{ marginBottom: "18px" }}><Badge /></div>
      <h1 style={{ fontSize: nameSz, fontWeight: "400", lineHeight: "1.05",
        letterSpacing: "-0.025em", color: C.white, margin: "0 0 10px", fontStyle: "italic",
        fontFamily: "'Georgia',serif" }}>
        {isMobile ? <><span>Suhaib</span><br /><span>Ahmed</span></> : "Suhaib Ahmed"}
      </h1>
      <p style={{ fontSize: "11px", fontFamily: "'Courier New',monospace", letterSpacing: "0.18em",
        textTransform: "uppercase", color: C.gold, margin: "0 0 14px" }}>
        Lead Full Stack Engineer · 15+ Years
      </p>
      <p style={{ fontSize: isMobile ? "14px" : "15px", lineHeight: "1.8", color: C.mutedLight, maxWidth: "560px" }}>
        Building high-performance, scalable applications for Fortune 500 companies across fintech, aviation, telecom, and enterprise tech.
      </p>
      <div style={{ marginTop: "20px" }}><Socials gap={18} /></div>
    </div>
  );
}

 function Portfolio() {
  useGlobalStyle();
  const bp = useBreakpoint();
  const isMobile = bp === "mobile";
  const isTablet = bp === "tablet";
  const isDesktop = bp === "desktop";

  const [active, setActive] = useState("about");
  const refs = useRef({});

  const scrollTo = useCallback(id => {
    refs.current[id]?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    const obs = [];
    NAV_SECTIONS.forEach(s => {
      const el = refs.current[s.toLowerCase()];
      if (!el) return;
      const o = new IntersectionObserver(
        ([e]) => { if (e.isIntersecting) setActive(s.toLowerCase()); },
        { threshold: isMobile ? 0.1 : 0.25 }
      );
      o.observe(el); obs.push(o);
    });
    return () => obs.forEach(o => o.disconnect());
  }, [isMobile]);

  const mainPad = isMobile
    ? "72px 20px 56px"
    : isTablet ? "88px 40px 80px"
    : "80px 64px 80px 80px";

  const secGap = isMobile ? "68px" : "96px";
  const expCols = isMobile ? "1fr" : "124px 1fr";

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text,
      fontFamily: "'Georgia','Times New Roman',serif", overflowX: "hidden" }}>

      {/* Noise texture */}
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0, opacity: 1,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.025'/%3E%3C/svg%3E")` }} />

      {/* Subtle radial accent top-left */}
      {isDesktop && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "600px", height: "600px", pointerEvents: "none", zIndex: 0,
          background: "radial-gradient(ellipse at 0% 0%, rgba(228,201,126,0.04) 0%, transparent 65%)" }} />
      )}

      {/* Headers */}
      {isMobile && <MobileHeader active={active} onNav={scrollTo} />}
      {isTablet && <TabletHeader active={active} onNav={scrollTo} />}

      {/* Layout */}
      <div style={{ position: "relative", zIndex: 1, display: "flex", maxWidth: "1240px", margin: "0 auto", minHeight: "100vh" }}>

        {/* ── DESKTOP SIDEBAR ── */}
        {isDesktop && (
          <aside style={{ width: "390px", minWidth: "390px", position: "sticky", top: 0, height: "100vh",
            display: "flex", flexDirection: "column", padding: "80px 40px 80px 60px",
            borderRight: `1px solid ${C.border}` }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "44px", height: "100%" }}>

              {/* Identity */}
              <div>
                <div style={{ marginBottom: "20px" }}><Badge /></div>
                <h1 style={{ fontSize: "50px", fontWeight: "400", lineHeight: "1.04",
                  letterSpacing: "-0.025em", color: C.white, margin: "0 0 12px", fontStyle: "italic",
                  fontFamily: "'Georgia',serif" }}>
                  Suhaib<br />Ahmed
                </h1>
                <p style={{ fontSize: "11px", fontFamily: "'Courier New',monospace", letterSpacing: "0.17em",
                  textTransform: "uppercase", color: C.gold, margin: "0 0 6px" }}>
                  Lead Full Stack Engineer
                </p>
                <p style={{ fontSize: "11px", fontFamily: "'Courier New',monospace", letterSpacing: "0.12em",
                  color: C.muted, margin: "0 0 18px" }}>
                  15 years · Fortune 500 scale
                </p>
                <p style={{ fontSize: "14px", lineHeight: "1.8", color: C.mutedLight }}>
                  Building high-performance applications across fintech, aviation, telecom, and enterprise tech.
                </p>
              </div>

              {/* Nav */}
              <nav style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
                {NAV_SECTIONS.map(s => {
                  const on = active === s.toLowerCase();
                  return (
                    <button key={s} className="sa-nbtn" onClick={() => scrollTo(s.toLowerCase())}
                      style={{ display: "flex", alignItems: "center", gap: "16px", background: "none",
                        border: "none", cursor: "pointer", padding: "8px 0", textAlign: "left" }}>
                      <span className="sa-nline" style={{ display: "block", height: "1px", flexShrink: 0,
                        width: on ? "52px" : "26px", background: on ? C.gold : C.muted, transition: "all 0.3s ease" }} />
                      <span className="sa-nlabel" style={{ fontSize: "10px", fontFamily: "'Courier New',monospace",
                        letterSpacing: "0.18em", textTransform: "uppercase",
                        color: on ? C.gold : C.muted, transition: "color 0.3s ease" }}>
                        {s}
                      </span>
                    </button>
                  );
                })}
              </nav>

              {/* Stats strip */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                {[["15+", "Years"], ["7", "Companies"], ["Fortune 500", "Scale"], ["Full Stack", "End-to-end"]].map(([val, lbl]) => (
                  <div key={lbl} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: "4px", padding: "10px 12px" }}>
                    <div style={{ fontSize: "14px", fontWeight: "500", color: C.white, fontFamily: "'Courier New',monospace" }}>{val}</div>
                    <div style={{ fontSize: "10px", color: C.muted, letterSpacing: "0.08em", marginTop: "2px" }}>{lbl}</div>
                  </div>
                ))}
              </div>

              <Socials gap={18} />
            </div>
          </aside>
        )}

        {/* ── MAIN ── */}
        <main style={{ flex: 1, padding: mainPad, minWidth: 0 }}>

          {/* Mobile/Tablet hero */}
          {(isMobile || isTablet) && <IdentityBlock isMobile={isMobile} />}

          {/* ── ABOUT ── */}
          <section ref={el => (refs.current["about"] = el)} id="about"
            className="sa-sec" style={{ marginBottom: secGap }}>
            <SectionHead text="About" />
            <p style={{ fontSize: isMobile ? "15px" : "16px", lineHeight: "1.85", color: C.mutedLight,
              marginBottom: "16px", maxWidth: "600px" }}>
              I'm a Lead Full Stack Engineer with 15+ years delivering production-grade applications at companies like{" "}
              <em style={{ color: C.text }}>Capital One, Apple, PayPal,</em> and{" "}
              <em style={{ color: C.text }}>Verizon</em>. I specialise in architecting systems that are fast,
              accessible, and built to scale — from pixel-perfect front-end interfaces to resilient cloud-backed APIs.
            </p>
            <p style={{ fontSize: isMobile ? "15px" : "16px", lineHeight: "1.85", color: C.mutedLight,
              marginBottom: "28px", maxWidth: "600px" }}>
              My stack spans{" "}
              <em style={{ color: C.text }}>React.js, Angular, Vue.js, React Native</em> on the client,{" "}
              <em style={{ color: C.text }}>Node.js and Python</em> on the server, and{" "}
              <em style={{ color: C.text }}>AWS</em> for infrastructure. I care deeply about system design,
              team culture, and shipping software that genuinely moves the needle.
            </p>

            {/* Skills */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "7px", maxWidth: "600px" }}>
              {SKILLS.map(sk => (
                <span key={sk} style={{ fontSize: "11px", fontFamily: "'Courier New',monospace",
                  letterSpacing: "0.07em", color: C.gold,
                  border: "1px solid rgba(228,201,126,0.22)", padding: "5px 12px", borderRadius: "3px",
                  background: C.goldGlow }}>
                  {sk}
                </span>
              ))}
            </div>
          </section>

          {/* ── EXPERIENCE ── */}
          <section ref={el => (refs.current["experience"] = el)} id="experience"
            className="sa-sec" style={{ marginBottom: secGap }}>
            <SectionHead text="Experience" />
            <div className="sa-exp-list" style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              {EXPERIENCES.map((exp, i) => (
                <div key={i} className="sa-exp-card clickable"
                  onClick={() => window.open(exp.companyUrl, "_blank")}
                  style={{ display: "grid", gridTemplateColumns: expCols,
                    gap: isMobile ? "4px" : "22px",
                    padding: isMobile ? "18px 12px" : "22px 20px", cursor: "pointer" }}>

                  {/* Period */}
                  <div style={{ fontSize: "10px", fontFamily: "'Courier New',monospace",
                    letterSpacing: "0.06em", color: C.muted,
                    paddingTop: isMobile ? "0" : "4px", marginBottom: isMobile ? "5px" : "0" }}>
                    {exp.period}
                  </div>

                  {/* Content */}
                  <div>
                    <h3 style={{ fontSize: isMobile ? "15px" : "16px", fontWeight: "400",
                      color: C.white, margin: "0 0 4px", lineHeight: "1.35",
                      display: "flex", alignItems: "center", flexWrap: "wrap", gap: "6px" }}>
                      {exp.role}
                      <span style={{ color: C.muted, fontStyle: "normal", fontSize: "13px" }}>·</span>
                      <a href={exp.companyUrl} target="_blank" rel="noreferrer"
                        className="sa-company-link"
                        onClick={e => e.stopPropagation()}
                        style={{ color: C.gold, textDecoration: "none", fontStyle: "italic",
                          display: "inline-flex", alignItems: "center", gap: "3px", transition: "opacity 0.15s" }}>
                        {exp.company}
                        <span className="sa-proj-arrow"><EXT size={11} /></span>
                      </a>
                    </h3>

                    {/* Metric pills */}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "5px", margin: "8px 0 10px" }}>
                      {exp.metrics.map(m => (
                        <span key={m} className="sa-metric">{m}</span>
                      ))}
                    </div>

                    <p style={{ fontSize: "14px", lineHeight: "1.78", color: C.mutedLight, margin: "0 0 12px" }}>
                      {exp.description}
                    </p>
                    <Tags tags={exp.tags} />
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ── PORTFOLIO ── */}
          <section ref={el => (refs.current["portfolio"] = el)} id="portfolio"
            className="sa-sec" style={{ marginBottom: secGap }}>
            <SectionHead text="Portfolio" />
            <div className="sa-proj-list" style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              {PROJECTS.map((proj, i) => {
                const href = proj.liveUrl || proj.companyUrl;
                return (
                  <a key={i} href={href} target="_blank" rel="noreferrer"
                    className="sa-proj-card"
                    style={{ display: "flex", gap: isMobile ? "14px" : "22px",
                      padding: isMobile ? "20px 12px" : "26px 20px",
                      alignItems: "flex-start", textDecoration: "none", color: "inherit" }}>

                    {/* Number */}
                    <div style={{ fontSize: isMobile ? "26px" : "32px", fontFamily: "'Georgia',serif",
                      fontStyle: "italic", color: "rgba(228,201,126,0.15)", lineHeight: "1",
                      flexShrink: 0, width: isMobile ? "32px" : "44px", paddingTop: "3px" }}>
                      0{i + 1}
                    </div>

                    {/* Body */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Title row */}
                      <div style={{ display: "flex", alignItems: "flex-start",
                        justifyContent: "space-between", gap: "10px", marginBottom: "6px", flexWrap: "wrap" }}>
                        <h3 style={{ fontSize: isMobile ? "14px" : "15px", fontWeight: "400",
                          color: C.white, margin: 0, lineHeight: "1.4", fontStyle: "italic",
                          flex: 1, minWidth: "140px", display: "flex", alignItems: "center", gap: "6px" }}>
                          {proj.title}
                          <span className="sa-proj-arrow" style={{ color: C.muted, flexShrink: 0 }}>
                            <ARR />
                          </span>
                        </h3>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
                          <span className="sa-stat">{proj.stat}</span>
                          {proj.liveUrl && (
                            <span className="sa-live-badge"
                              onClick={e => { e.preventDefault(); window.open(proj.liveUrl, "_blank"); }}>
                              <EXT size={10} /> Live
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Company */}
                      <p style={{ fontSize: "11px", fontFamily: "'Courier New',monospace",
                        letterSpacing: "0.08em", color: C.muted, margin: "0 0 8px" }}>
                        {proj.company}
                      </p>

                      <p style={{ fontSize: "14px", lineHeight: "1.78", color: C.mutedLight, margin: "0 0 12px" }}>
                        {proj.description}
                      </p>
                      <Tags tags={proj.tags} />
                    </div>
                  </a>
                );
              })}
            </div>
          </section>

          {/* ── FOOTER ── */}
          <footer style={{ borderTop: `1px solid ${C.border}`, paddingTop: "36px", marginTop: "8px",
            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "16px" }}>
            <p style={{ fontSize: "11px", color: C.muted, fontFamily: "'Courier New',monospace",
              letterSpacing: "0.05em", lineHeight: "1.7" }}>
              Designed & engineered by <em style={{ color: C.mutedLight }}>Suhaib Ahmed</em>
            </p>
            <a href="https://www.linkedin.com/in/suhaib-siddiqui?utm_source=share_via&utm_content=profile&utm_medium=member_ios" target="_blank" rel="noreferrer"
              style={{ fontSize: "10px", fontFamily: "'Courier New',monospace", letterSpacing: "0.12em",
                color: C.gold, textDecoration: "none", border: `1px solid ${C.goldDim}`,
                padding: "4px 12px", borderRadius: "3px", background: C.goldGlow,
                display: "flex", alignItems: "center", gap: "6px" }}>
              <LI /> Connect on LinkedIn
            </a>
          </footer>

        </main>
      </div>
    </div>
  );
}


function App() {
  return (
    <Portfolio/>
  );
}

export default App;
